import React from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { HiXCircle } from 'react-icons/hi';
import uploadcare from 'uploadcare-widget';
import { toastError } from 'react-geek-toast';
import Button from 'partial/components/Button';
import { BiCloudUpload } from 'react-icons/bi';
import DropZone from '../components/DropZone';

export const uploadPhoto = (options) =>
  new Promise((resolve) => {
    const opt = {
      tabs: ['file', 'url', 'camera'],
      ...options,
    };
    uploadcare
      .openDialog(null, {
        publicKey: process.env.REACT_APP_UPLOADCARE_KEY || 'demopublickey',
        ...opt,
        imagesOnly: true,
      })
      .done((file) => {
        file.done((x) => {
          if ((x?.size / 1024).toFixed(0) >= 10240) {
            toastError('The uploaded file exceeds 10MB');
            return;
          }
          resolve(x.cdnUrl);
        });
      })
      .fail(() => {
        resolve(null);
      });
  });

export const uploadPhotos = (files, options) =>
  new Promise((resolve) => {
    const opt = {
      publicKey: process.env.REACT_APP_UPLOADCARE_KEY || 'demopublickey',
      // tabs: ['file', 'url', 'camera'],
      // crop: '1:1',
      ...options,
    };
    Promise.all(
      files.map(
        (file) =>
          new Promise((r) => {
            uploadcare.fileFrom('object', file, opt).then(r);
          })
      )
    ).then((out) => {
      resolve(out.map((x) => x.cdnUrl));
    });
  });

function FormPhotoDnd({
  id,
  label,
  required,
  value,
  name,
  onChange,
  onSetFieldValue,
  className,
  error,
}) {
  const isError = !!error;
  const [isUploading, setIsUploading] = React.useState(false);
  const handleDrop = async (files, onIsUploading) => {
    onIsUploading(true);
    setIsUploading(true);
    const [output] = await uploadPhotos(files.filter((x, i) => i < 1));
    onIsUploading(false);
    setIsUploading(false);
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: output,
      }));
    }
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, output);
    }
  };
  const handleUpload = async () => {
    setIsUploading(true);
    const output = await uploadPhoto();
    setIsUploading(false);
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: output,
      }));
    }
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, output);
    }
  };
  const handleRemove = () => {
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: '',
      }));
    }
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, '');
    }
  };

  return (
    <div className="flex flex-col">
      <label
        htmlFor={id}
        className="block text-xs font-medium mb-2 text-gray-500"
      >
        <span>{label}</span>
        {required && <span className="ml-1 font-light text-red-500">*</span>}
      </label>
      <DropZone onDrop={handleDrop}>
        <div
          className={cn(
            'w-full rounded-lg bg-gray-100 relative z-[1]',
            isError
              ? 'border border-red-300 focus-within:ring-red-500 focus-within:border-red-500 animate-wiggle'
              : 'border-2 border-gray-300 border-dashed',
            className
          )}
        >
          <div className="h-full w-full px-3 pt-2 pb-3 flex flex-col">
            {!value ? (
              <div className="flex-1 flex">
                <div className="m-auto flex flex-col items-center justify-center">
                  <BiCloudUpload className="h-16 w-16 text-gray-300" />
                  <div className="m-auto text-gray-400 text-xs font-light mb-3">
                    Drag and Drop image here
                  </div>
                  <Button
                    size="sm"
                    onClick={handleUpload}
                    disabled={isUploading}
                  >
                    {isUploading ? 'Uploading...' : 'Choose File'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex-1 bg-gray-200 rounded-lg flex relative">
                <div className="absolute z-10 top-0 right-0 p-1">
                  <button
                    className="text-red-500 hover:text-red-600 transition"
                    type="button"
                    onClick={handleRemove}
                  >
                    <HiXCircle className="h-8 w-8 bg-red-50 p-1 mt-1 mr-1 rounded-full" />
                  </button>
                </div>
                <img
                  className="absolute h-full w-full object-contain rounded-xl"
                  src={value}
                  alt={label}
                />
              </div>
            )}
          </div>
        </div>
      </DropZone>
    </div>
  );
}

FormPhotoDnd.defaultProps = {
  id: '',
  value: '',
  required: false,
  error: null,
  className: 'aspect-h-2 aspect-w-3',
  onChange: false,
  onSetFieldValue: false,
};

FormPhotoDnd.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
};

export default FormPhotoDnd;
